import React, { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Grid,
  Typography,
  useMediaQuery,
  Divider,
  Checkbox,
} from "@material-ui/core";
import { EmojiEvents } from "@material-ui/icons";
import {
  COLORS,
  HEIRLOOM_ORDER,
  HEIRLOOMS,
  PRESTIGE_SKINS,
  PRESTIGE_SKINS_ORDER,
} from "../../types";
import useLocalStorage from "../../hooks/useLocalStorage";
import { clone } from "lodash";

export default ({
  totalPacks,
  heirloomRecord,
  setHeirloomRecord,
  setShowConfetti,
  setShowSnackbar,
}) => {
  // TERMINOLOGY
  // - totalPacks = total number of apex the player has based on their input
  // - heirloomRecord = the localStorage powered in-memory last-heirloom record bit

  // heirloom collection
  const [heirloomCollection, setHeirloomCollection] = useLocalStorage(
    "heirloomCollection",
    {}
  );
  const [prestigeSkinCollection, setPrestigeSkinCollection] = useLocalStorage(
    "prestigeSkinCollection",
    {}
  );
  const [dimHeirloomCollection, setDimHeirloomCollection] = useState(false);
  const [dimPrestigeSkins, setDimPrestigeSkins] = useState(false);

  const [newRecordedHeirloomBaseLevel, setNewRecordedHeirloomBaseLevel] =
    useState(heirloomRecord);
  const [editMode, setEditMode] = useState(false);

  const recordHeirloom = (numPacksAtHeirloomOpen) => {
    // first heirloom
    if (heirloomRecord === null) {
      setNewRecordedHeirloomBaseLevel(totalPacks); // set the display
      setHeirloomRecord(totalPacks); // set the storage record
      setShowConfetti(true); // party
      return;
    }

    setHeirloomRecord(parseInt(numPacksAtHeirloomOpen)); // set the long storage
    setNewRecordedHeirloomBaseLevel(parseInt(numPacksAtHeirloomOpen));
    setShowConfetti(true); // party
  };

  // checks when newRecordedHeirloomBaseLevel changes to ask the user if we should commit the edit to memory
  useEffect(() => {
    // no update required
    if (newRecordedHeirloomBaseLevel === heirloomRecord) {
      setEditMode(false);
      return;
    }

    // assume here the new value and the record values are different
    setEditMode(true);

    // in the UI flip around the text and show a confirmation button
  }, [newRecordedHeirloomBaseLevel, heirloomRecord]);

  const stackedView = useMediaQuery("(max-width:600px)");

  const updateCollection = (thisLegend) => {
    const tempObj = clone(heirloomCollection);

    const existingRecord = tempObj[thisLegend];

    if (existingRecord === undefined) {
      tempObj[thisLegend] = true;
    } else {
      tempObj[thisLegend] = !tempObj[thisLegend];
    }

    setHeirloomCollection(tempObj);
  };
  const updateSkins = (thisLegend) => {
    const tempObj = clone(prestigeSkinCollection);

    const existingRecord = tempObj[thisLegend];

    if (existingRecord === undefined) {
      tempObj[thisLegend] = true;
    } else {
      tempObj[thisLegend] = !tempObj[thisLegend];
    }

    setPrestigeSkinCollection(tempObj);
  };

  // update dimming based on collection status
  useEffect(() => {
    let allUnchecked = true;

    for (const legend in heirloomCollection) {
      if (heirloomCollection[legend] === true) {
        allUnchecked = false;
      }
    }

    setDimHeirloomCollection(!!allUnchecked ? false : true);
  }, [heirloomCollection]);
  useEffect(() => {
    let allUnchecked = true;

    for (const legend in prestigeSkinCollection) {
      if (prestigeSkinCollection[legend] === true) {
        allUnchecked = false;
      }
    }

    setDimPrestigeSkins(!!allUnchecked ? false : true);
  }, [prestigeSkinCollection]);

  return (
    <div className="width-constrained">
      <Grid
        container
        spacing={3}
        justify="center"
        alignItems="flex-start"
        style={{
          marginTop: "1em",
        }}
      >
        <Grid
          item
          xs={12}
          sm={6}
          md={4}
          lg={4}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems:
              heirloomRecord !== null && !stackedView ? "flex-end" : "center",
          }}
        >
          <Button
            onClick={() => recordHeirloom(totalPacks)}
            variant="contained"
            color="primary"
            data-cy="button-record-heirloom"
            disabled={totalPacks === newRecordedHeirloomBaseLevel || !!editMode}
            size="large"
            style={{ display: "flex" }}
          >
            <EmojiEvents
              style={{
                color:
                  totalPacks === newRecordedHeirloomBaseLevel || !!editMode
                    ? "inherit"
                    : COLORS.LEGENDARY,
                marginRight: 6,
              }}
            />
            HEIRLOOM ACQUIRED
            <EmojiEvents
              style={{
                color:
                  totalPacks === newRecordedHeirloomBaseLevel || !!editMode
                    ? "inherit"
                    : COLORS.LEGENDARY,
                marginLeft: 4,
              }}
            />
          </Button>
        </Grid>
        {heirloomRecord !== null && (
          <Grid item xs={12} sm={6} md={4} lg={4} style={{ textAlign: "left" }}>
            <TextField
              label="Apex Packs Held at Last Heirloom"
              value={newRecordedHeirloomBaseLevel}
              onChange={(event) => {
                const newVal = parseInt(event.target.value);
                if (newVal < 0) {
                  // guard negatives
                  setNewRecordedHeirloomBaseLevel(0);
                  return;
                }
                if (newVal > totalPacks) {
                  // guard more than total packs (impossibility)
                  setNewRecordedHeirloomBaseLevel(totalPacks);
                  return;
                }

                setNewRecordedHeirloomBaseLevel(newVal); // update the value
              }}
              helperText={
                !editMode
                  ? "The total number of Apex Packs you had when you opened your last set of Heirloom Shards"
                  : `[EDITING] Original Value: ${heirloomRecord} packs`
              }
              style={{ width: "100%" }}
              type="number"
              variant="outlined"
              inputProps={{ pattern: "\\d*" }}
              data-cy="last-heirloom-at-field"
            />

            {!!editMode && (
              <div style={{ display: "flex", marginTop: "1em" }}>
                <Button
                  variant="outlined"
                  color="primary"
                  data-cy="button-heirlooms-update"
                  onClick={() => {
                    setHeirloomRecord(parseInt(newRecordedHeirloomBaseLevel)); // commit the new value back to localStorage
                    setEditMode(false); // turn off edit mode
                    setShowSnackbar("Successfully updated last packs value");
                  }}
                  style={{ marginRight: "1em" }}
                >
                  Update
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  data-cy="button-heirlooms-reset"
                  onClick={() => {
                    setNewRecordedHeirloomBaseLevel(heirloomRecord); // reset back to the in-memory value
                    setEditMode(false); // turn off edit mode
                    setShowSnackbar(
                      "Reverted to previously stored packs value"
                    ); // snackbar pop-up
                  }}
                >
                  Cancel
                </Button>
              </div>
            )}

            {!!editMode && (
              <small
                style={{
                  display: "block",
                  marginTop: "1em",
                  fontWeight: "bold",
                }}
              >
                Manually editing the <i>Apex Packs at Last Heirloom</i> field
                will <strong>edit</strong> the point at which you received your
                last heirloom.
              </small>
            )}
          </Grid>
        )}
      </Grid>

      {heirloomRecord !== null && (
        <>
          <Divider
            className="width-constrained"
            style={{ marginTop: 40, marginBottom: 48 }}
          />

          <Typography variant="h5">Heirloom Collection</Typography>
          <Grid
            container
            spacing={3}
            justify="flex-start"
            alignItems="flex-start"
            style={{ marginTop: "2em", marginBottom: "2em" }}
          >
            {HEIRLOOM_ORDER.map((thisLegend, i) => (
              <Grid
                item
                xs={6}
                sm={4}
                md={3}
                lg={3}
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "flex-start",
                  opacity:
                    !!heirloomCollection[thisLegend] || !dimHeirloomCollection
                      ? 1
                      : 0.5,
                  transition: "opacity 300ms ease-in-out 0ms",
                }}
                key={i}
              >
                <Checkbox
                  color="secondary"
                  checked={
                    heirloomCollection[thisLegend] === undefined
                      ? false
                      : heirloomCollection[thisLegend]
                  }
                  onChange={() => updateCollection(thisLegend)}
                  data-cy=""
                />

                <div
                  style={{
                    flexDirection: "column",
                    paddingTop: 9,
                    paddingBottom: 9,
                  }}
                >
                  {thisLegend}
                  <br />
                  <span style={{ color: COLORS.HEIRLOOM, fontWeight: "bold" }}>
                    {HEIRLOOMS[thisLegend]}
                  </span>
                </div>
              </Grid>
            ))}
          </Grid>

          <Typography variant="h6" style={{ marginTop: "2em" }}>
            Prestige Skins
          </Typography>
          <Grid
            container
            spacing={3}
            justify="flex-start"
            alignItems="flex-start"
            style={{ marginTop: 0, marginBottom: "4em" }}
          >
            {PRESTIGE_SKINS_ORDER.map((thisLegend, i) => (
              <Grid
                item
                xs={6}
                sm={4}
                md={3}
                lg={3}
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "flex-start",
                  opacity:
                    !!prestigeSkinCollection[thisLegend] || !dimPrestigeSkins
                      ? 1
                      : 0.5,
                  transition: "opacity 300ms ease-in-out 0ms",
                }}
                key={i}
              >
                <Checkbox
                  color="secondary"
                  checked={
                    prestigeSkinCollection[thisLegend] === undefined
                      ? false
                      : prestigeSkinCollection[thisLegend]
                  }
                  onChange={() => updateSkins(thisLegend)}
                  data-cy=""
                />

                <div
                  style={{
                    flexDirection: "column",
                    paddingTop: 9,
                    paddingBottom: 9,
                  }}
                >
                  {thisLegend}
                  <br />
                  <span style={{ color: COLORS.HEIRLOOM, fontWeight: "bold" }}>
                    {PRESTIGE_SKINS[thisLegend]}
                  </span>
                </div>
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </div>
  );
};
